import { useCallback, useContext, useEffect, useReducer } from "react";

import { OutletListDTO } from "@zenith/services/user";
import { GLOBAL_CONTEXT_ACTION_TYPE } from "./actions";
import { hydrateGlobalContext, persistGlobalContext } from "./persistence";
import {
  GlobalContextDispatchType,
  GlobalContextType,
  LoadingContextHookType,
  LoansContextHookType,
  LoansContextType,
  MobileVerificationContextHookType,
  NotificationContextHookType,
  PaymentLinkContextHookType,
  PaymentLinkContextType,
  PaymentsContextHookType,
  PaymentsContextType,
  PosContextHookType,
  PosContextType,
  RegistrationContextHookType,
  RegistrationContextType,
  SelfiesContextHookType,
  SelfiesContextType,
  SendMoneyContextHookType,
  SendMoneyContextType,
  ServiceFailureContextHookType,
  AppStepperContextHookType,
  TransactionsContextHookType,
  TransactionsContextType,
  UserDetailsContextHookType,
  UserDetailsContextType,
  GlobalContextActionsType,
  SettingsContextType,
  SettingsContextHookType,
  SignUpContextType,
  SignUpContextHookType,
  PersonalDetailsContextHookType,
  PersonalDetailsContextType,
  UpgradeContextHookType,
  UpgradeContextType,
  ZenithAccountUpgradeContextHookType,
  OutletContextHookType,
  OutletContextType,
  OutletSelection,
} from "./types";
import { globalContextReducer } from "./reducer";
import { GlobalContext } from "./globalContext";

/**
 * The Global Context hook.
 * @param initGlobalContext The initial state of the Global Context.
 * @returns The Global Context and Global Context Dispatch functions.
 */
export const useGlobalContext = (initGlobalContext: GlobalContextType) => {
  const [globalContext, dispatch] = useReducer(
    globalContextReducer,
    initGlobalContext,
    hydrateGlobalContext
  );

  // Keeps signUpContext/mobileVerificationContext in sessionStorage so a
  // reload doesn't lose them. clearAllContexts (logout, Sign In page
  // mount, token invalidation) resets them to their empty defaults, which
  // this then persists too, clearing storage as a side effect — no
  // separate cleanup path needed for a different user on the same device.
  useEffect(() => {
    persistGlobalContext(globalContext);
  }, [globalContext]);

  const clearAllContexts = useCallback(() => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_ALL_CONTEXTS,
    });
  }, []);

  const clearUnusedContexts = useCallback(() => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_UNUSED_CONTEXTS,
    });
  }, []);

  const setLoading = useCallback((payload: boolean) => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.SET_LOADING,
      payload,
    });
  }, []);

  const setIsUpgrade = useCallback((payload: boolean) => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.SET_IS_UPGRADE,
      payload,
    });
  }, []);

  const setAccountNumber = useCallback((payload: string) => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.SET_ACCOUNT_NUMBER,
      payload,
    });
  }, []);

  const setCountryCode = useCallback((payload: string) => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.SET_COUNTRY_CODE,
      payload,
    });
  }, []);

  const setEmail = useCallback((payload: string) => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.SET_EMAIL,
      payload,
    });
  }, []);

  const setPhoneNumber = useCallback((payload: string) => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.SET_PHONE_NUMBER,
      payload,
    });
  }, []);

  const setOpen = useCallback((payload: boolean) => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.SET_OPEN_NOTIFICATION,
      payload,
    });
  }, []);

  const clearNotificationContext = useCallback(() => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_NOTIFICATION_CONTEXT,
    });
  }, []);

  const setActiveStep = useCallback((payload: number) => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.SET_ACTIVE_STEP,
      payload,
    });
  }, []);

  const setMaxStep = useCallback((payload: number) => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.SET_MAX_STEP,
      payload,
    });
  }, []);

  const setPaymentsContext = useCallback((payload: PaymentsContextType) => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.SET_PAYMENTS_CONTEXT,
      payload,
    });
  }, []);

  const clearPaymentsContext = useCallback(() => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_PAYMENTS_CONTEXT,
    });
  }, []);

  const setServiceFailureContext = useCallback((payload: boolean) => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.SET_SERVICE_FAILURE,
      payload,
    });
  }, []);

  const setFirstName = useCallback((payload: string) => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.SET_FIRST_NAME,
      payload,
    });
  }, []);

  const setIsNewUser = useCallback((payload: boolean) => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.SET_IS_NEW_USER,
      payload,
    });
  }, []);

  const setSendMoneyContext = useCallback((payload: SendMoneyContextType) => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.SET_SEND_MONEY_CONTEXT,
      payload,
    });
  }, []);

  const clearSendMoneyContext = useCallback(() => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_SEND_MONEY_CONTEXT,
    });
  }, []);

  const setTransactionsContext = useCallback(
    (payload: TransactionsContextType) => {
      dispatch({
        type: GLOBAL_CONTEXT_ACTION_TYPE.SET_TRANSACTIONS_CONTEXT,
        payload,
      });
    },
    []
  );

  const clearTransactionsContext = useCallback(() => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_TRANSACTIONS_CONTEXT,
    });
  }, []);

  const setSelfiesContext = useCallback((payload: SelfiesContextType[]) => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.SET_SELFIES_CONTEXT,
      payload,
    });
  }, []);

  const setPaymentLinkContext = useCallback(
    (payload: PaymentLinkContextType) => {
      dispatch({
        type: GLOBAL_CONTEXT_ACTION_TYPE.SET_PAYMENT_LINK_CONTEXT,
        payload,
      });
    },
    []
  );

  const clearPaymentLinkContext = useCallback(() => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_PAYMENT_LINK_CONTEXT,
    });
  }, []);

  const setPosContext = useCallback((payload: PosContextType) => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.SET_POS_CONTEXT,
      payload,
    });
  }, []);

  const clearPosContext = useCallback(() => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_POS_CONTEXT,
    });
  }, []);

  const setLoansContext = useCallback((payload: LoansContextType) => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.SET_LOANS_CONTEXT,
      payload,
    });
  }, []);

  const clearLoansContext = useCallback(() => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_LOANS_CONTEXT,
    });
  }, []);

  const setRegistrationContext = useCallback(
    (payload: RegistrationContextType) => {
      dispatch({
        type: GLOBAL_CONTEXT_ACTION_TYPE.SET_REGISTRATION_CONTEXT,
        payload,
      });
    },
    []
  );

  const setSignUpContext = useCallback(
    (payload: Partial<SignUpContextType>) => {
      dispatch({
        type: GLOBAL_CONTEXT_ACTION_TYPE.SET_SIGNUP_CONTEXT,
        payload,
      });
    },
    []
  );

  const setUserDetailsContext = useCallback(
    (payload: Partial<UserDetailsContextType>) => {
      dispatch({
        type: GLOBAL_CONTEXT_ACTION_TYPE.SET_USER_DETAILS_CONTEXT,
        payload,
      });
    },
    []
  );

  const setSettingsContext = useCallback((payload: SettingsContextType) => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.SET_SETTINGS_CONTEXT,
      payload,
    });
  }, []);

  const clearSettingsContext = useCallback(() => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_SETTINGS_CONTEXT,
    });
  }, []);

  const setPersonalDetailsContext = useCallback(
    (payload: PersonalDetailsContextType) => {
      dispatch({
        type: GLOBAL_CONTEXT_ACTION_TYPE.SET_PERSONAL_DETAILS_CONTEXT,
        payload,
      });
    },
    []
  );

  const clearPersonalDetailsContext = useCallback(() => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_PERSONAL_DETAILS_CONTEXT,
    });
  }, []);

  const setUpgradeContext = useCallback((payload: UpgradeContextType) => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.SET_UPGRADE_CONTEXT,
      payload,
    });
  }, []);

  const clearUpgradeContext = useCallback(() => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_UPGRADE_CONTEXT,
    });
  }, []);

  const setOutletContext = useCallback(
    (payload: Partial<OutletContextType>) => {
      dispatch({
        type: GLOBAL_CONTEXT_ACTION_TYPE.SET_OUTLET_CONTEXT,
        payload,
      });
    },
    []
  );

  const setOutlets = useCallback((payload: OutletListDTO[]) => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.SET_OUTLETS,
      payload,
    });
  }, []);

  const setOutletSelection = useCallback((payload: OutletSelection) => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.SET_OUTLET_SELECTION,
      payload,
    });
  }, []);

  const clearOutletContext = useCallback(() => {
    dispatch({
      type: GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_OUTLET_CONTEXT,
    });
  }, []);

  const globalContextDispatch: GlobalContextDispatchType = {
    clearAllContexts,
    clearUnusedContexts,
    setLoading,
    setIsUpgrade,
    setAccountNumber,
    setCountryCode,
    setEmail,
    setPhoneNumber,
    setFirstName,
    setIsNewUser,
    setOpen,
    clearNotificationContext,
    setActiveStep,
    setMaxStep,
    setPaymentsContext,
    clearPaymentsContext,
    setServiceFailureContext,
    setSendMoneyContext,
    clearSendMoneyContext,
    setTransactionsContext,
    clearTransactionsContext,
    setSelfiesContext,
    setPosContext,
    clearPosContext,
    setRegistrationContext,
    setSignUpContext,
    setUserDetailsContext,
    setPaymentLinkContext,
    clearPaymentLinkContext,
    setLoansContext,
    clearLoansContext,
    setSettingsContext,
    clearSettingsContext,
    setPersonalDetailsContext,
    clearPersonalDetailsContext,
    setUpgradeContext,
    clearUpgradeContext,
    setOutletContext,
    setOutlets,
    setOutletSelection,
    clearOutletContext,
  };

  return {
    globalContext,
    globalContextDispatch,
  };
};

/**
 * The Global Context Actions hook.
 * It uses the Global Context, and returns only the actions to use
 * on the global context.
 * @returns The Global Context Actions.
 */
export const useGlobalContextActions = (): GlobalContextActionsType => {
  const {
    globalContextDispatch: { clearAllContexts, clearUnusedContexts },
  } = useContext(GlobalContext);

  return { clearAllContexts, clearUnusedContexts };
};

/**
 * The Loading Context hook.
 * It uses the Global Context, and returns only the 'loadingContext'
 * and necessary hooks.
 * @returns The Loading Context and necessary hooks.
 */
export const useLoadingContext = (): LoadingContextHookType => {
  const {
    globalContext: { loadingContext },
    globalContextDispatch: { setLoading },
  } = useContext(GlobalContext);

  return { loadingContext, setLoading };
};

/**
 * The Mobile Verification Context hook.
 * It uses the Global Context, and returns only the 'mobileVerificationContext'
 * and necessary hooks.
 * @returns The Mobile Verification Context and necessary hooks.
 */
export const useMobileVerificationContext =
  (): MobileVerificationContextHookType => {
    const {
      globalContext: { mobileVerificationContext },
      globalContextDispatch: {
        setCountryCode,
        setEmail,
        setPhoneNumber,
        setFirstName,
        setIsNewUser,
      },
    } = useContext(GlobalContext);

    return {
      mobileVerificationContext,
      setCountryCode,
      setEmail,
      setPhoneNumber,
      setFirstName,
      setIsNewUser,
    };
  };

/**
 * The Zenith Account Context hook.
 * It uses the Global Context, and returns only the 'zenithAccountUpgradeContext'
 * and necessary hooks.
 * @returns The Zenith Account Context and necessary hooks.
 */
export const useZenithAccountUpgradeContext =
  (): ZenithAccountUpgradeContextHookType => {
    const {
      globalContext: { zenithAccountUpgradeContext },
      globalContextDispatch: { setIsUpgrade, setAccountNumber },
    } = useContext(GlobalContext);

    return {
      zenithAccountUpgradeContext,
      setIsUpgrade,
      setAccountNumber,
    };
  };

/**
 * The Notification Context hook.
 * It uses the Global Context, and returns only the 'notificationContext'
 * and necessary hooks.
 * @returns The Notification Context and necessary hooks.
 */
export const useNotificationContext = (): NotificationContextHookType => {
  const {
    globalContext: { notificationContext },
    globalContextDispatch: { clearNotificationContext, setOpen },
  } = useContext(GlobalContext);

  return {
    notificationContext,
    clearNotificationContext,
    setOpen,
  };
};

/**
 * The Sign Up Stepper Context hook.
 * It uses the Global Context, and returns only the 'appStepperContext'
 * and necessary hooks.
 * @returns The Sign Up Stepper Context and necessary hooks.
 */
export const useAppStepperContext = (): AppStepperContextHookType => {
  const {
    globalContext: { appStepperContext },
    globalContextDispatch: { setActiveStep, setMaxStep },
  } = useContext(GlobalContext);

  return {
    appStepperContext,
    setActiveStep,
    setMaxStep,
  };
};

/**
 * The Payments Context hook.
 * It uses the Global Context, and returns only the 'paymentsContext'
 * and necessary hooks.
 * @returns The Payments Context and necessary hooks.
 */
export const usePaymentsContext = (): PaymentsContextHookType => {
  const {
    globalContext: { paymentsContext },
    globalContextDispatch: { setPaymentsContext, clearPaymentsContext },
  } = useContext(GlobalContext);

  return {
    paymentsContext,
    setPaymentsContext,
    clearPaymentsContext,
  };
};

/**
 * The Service Failure hook.
 * It uses the Global Context, and returns only the 'serviceFailureContext'
 * and necessary hooks.
 * @returns The Service Failure and necessary hooks.
 */
export const useServiceFailureContext = (): ServiceFailureContextHookType => {
  const {
    globalContext: { serviceFailureContext },
    globalContextDispatch: { setServiceFailureContext },
  } = useContext(GlobalContext);
  return {
    serviceFailureContext,
    setServiceFailureContext,
  };
};

/**
 * The Send Money Context hook.
 * It uses the Global Context, and returns only the 'sendMoneyContext'
 * and necessary hooks.
 * @returns {SendMoneyContextHookType} The Send Money Context and necessary hooks.
 */
export const useSendMoneyContext = (): SendMoneyContextHookType => {
  const {
    globalContext: { sendMoneyContext },
    globalContextDispatch: { setSendMoneyContext, clearSendMoneyContext },
  } = useContext(GlobalContext);

  return {
    sendMoneyContext,
    setSendMoneyContext,
    clearSendMoneyContext,
  };
};

/**
 * The Transactions Context hook.
 * It uses the Global Context, and returns only the 'transactionsContext'
 * and necessary hooks.
 * @returns {TransactionsContextHookType} The Transactions Context and necessary hooks.
 */
export const useTransactionsContext = (): TransactionsContextHookType => {
  const {
    globalContext: { transactionsContext },
    globalContextDispatch: { setTransactionsContext, clearTransactionsContext },
  } = useContext(GlobalContext);

  return {
    transactionsContext,
    setTransactionsContext,
    clearTransactionsContext,
  };
};

/**
 * The Selfies Context hook.
 * It uses the Global Context, and returns only the 'selfiesContext'
 * and necessary hooks.
 * @returns {SelfiesContextHookType} The Selfies Context and necessary hooks.
 */
export const useSelfiesContext = (): SelfiesContextHookType => {
  const {
    globalContext: { selfiesContext },
    globalContextDispatch: { setSelfiesContext },
  } = useContext(GlobalContext);

  return {
    selfiesContext,
    setSelfiesContext,
  };
};

/**
 * The POS Context hook.
 * It uses the Global Context, and returns only the 'posContext'
 * and necessary hooks.
 * @returns {PosContextHookType} The Selfies Context and necessary hooks.
 */
export const usePosContext = (): PosContextHookType => {
  const {
    globalContext: { posContext },
    globalContextDispatch: { setPosContext, clearPosContext },
  } = useContext(GlobalContext);

  return {
    posContext,
    setPosContext,
    clearPosContext,
  };
};

/**
 * The Registration Context hook.
 * It uses the Global Context, and returns only the 'registrationContext'
 * and necessary hooks.
 * @returns {RegistrationContextHookType} The Registration Context and necessary hooks.
 */
export const useRegistrationContext = (): RegistrationContextHookType => {
  const {
    globalContext: { registrationContext },
    globalContextDispatch: { setRegistrationContext },
  } = useContext(GlobalContext);

  return {
    registrationContext,
    setRegistrationContext,
  };
};

/**
 * The SignUp Context hook.
 * It uses the Global Context, and returns only the 'signUpContext'
 * and necessary hooks.
 * @returns {SignUpContextHookType} The SignUp Context and necessary hooks.
 */
export const useSignUpContext = (): SignUpContextHookType => {
  const {
    globalContext: { signUpContext },
    globalContextDispatch: { setSignUpContext },
  } = useContext(GlobalContext);

  return {
    signUpContext,
    setSignUpContext,
  };
};

/**
 * The User Details Context hook.
 * It uses the Global Context, and returns only the 'userDetailsContext'
 * and necessary hooks.
 * @returns {UserDetailsContextHookType} The User Details Context and necessary hooks.
 */
export const useUserDetailsContext = (): UserDetailsContextHookType => {
  const {
    globalContext: { userDetailsContext },
    globalContextDispatch: { setUserDetailsContext },
  } = useContext(GlobalContext);

  return {
    userDetailsContext,
    setUserDetailsContext,
  };
};

/** The Payment Link Context hook.
 * It uses the Global Context, and returns only the 'paymentLinkContext'
 * and necessary hooks.
 * @returns {PaymentLinkContextHookType} The Payment Link Context and necessary hooks.
 */
export const usePaymentLinkContext = (): PaymentLinkContextHookType => {
  const {
    globalContext: { paymentLinkContext },
    globalContextDispatch: { setPaymentLinkContext, clearPaymentLinkContext },
  } = useContext(GlobalContext);

  return {
    paymentLinkContext,
    setPaymentLinkContext,
    clearPaymentLinkContext,
  };
};

/**
 * The Loans Context hook.
 * It uses the Global Context, and returns only the 'loansContext'
 * and necessary hooks.
 * @returns {LoansContextHookType} The Loans Context and necessary hooks.
 */
export const useLoansContext = (): LoansContextHookType => {
  const {
    globalContext: { loansContext },
    globalContextDispatch: { setLoansContext, clearLoansContext },
  } = useContext(GlobalContext);

  return {
    loansContext,
    setLoansContext,
    clearLoansContext,
  };
};

/**
 * The Settings Context hook.
 * It uses the Global Context, and returns only the 'settingsContext'
 * and necessary hooks.
 * @returns {SettingsContextHookType} The Settings Context and necessary hooks.
 */
export const useSettingsContext = (): SettingsContextHookType => {
  const {
    globalContext: { settingsContext },
    globalContextDispatch: { setSettingsContext, clearSettingsContext },
  } = useContext(GlobalContext);

  return {
    settingsContext,
    setSettingsContext,
    clearSettingsContext,
  };
};

/**
 * The Personal Details Context hook.
 * It uses the Global Context, and returns only the 'personalDetailsContext'
 * and necessary hooks.
 * @returns {PersonalDetailsContextHookType} The Personal Details Context and necessary hooks.
 */
export const usePersonalDetailsContext = (): PersonalDetailsContextHookType => {
  const {
    globalContext: { personalDetailsContext },
    globalContextDispatch: {
      setPersonalDetailsContext,
      clearPersonalDetailsContext,
    },
  } = useContext(GlobalContext);

  return {
    personalDetailsContext,
    setPersonalDetailsContext,
    clearPersonalDetailsContext,
  };
};

/**
 * The Upgrade Context hook.
 * It uses the Global Context, and returns only the 'upgradeContext'
 * and necessary hooks.
 * @returns {UpgradeContextHookType} The Personal Details Context and necessary hooks.
 */
export const useUpgradeContext = (): UpgradeContextHookType => {
  const {
    globalContext: { upgradeContext },
    globalContextDispatch: { setUpgradeContext, clearUpgradeContext },
  } = useContext(GlobalContext);

  return {
    upgradeContext,
    setUpgradeContext,
    clearUpgradeContext,
  };
};

/**
 * The Outlet Context hook.
 * It uses the Global Context, and returns only the 'outletContext'
 * and necessary hooks.
 * @returns {OutletContextHookType} The Outlet Context and necessary hooks.
 */
export const useOutletContext = (): OutletContextHookType => {
  const {
    globalContext: { outletContext },
    globalContextDispatch: {
      setOutletContext,
      setOutlets,
      setOutletSelection,
      clearOutletContext,
    },
  } = useContext(GlobalContext);

  return {
    outletContext,
    setOutletContext,
    setOutlets,
    setOutletSelection,
    clearOutletContext,
  };
};
